import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';

// Collapsible sections on the patient Details view. `clinical` is the enriched
// header strip (MRN / Insurance / Emergency / etc.) rendered inside the
// identity Paper; the other three are the info cards below it. They share one
// per-user store so a single "Expand all / Collapse all" control and the
// individual toggles stay in sync.
export type SectionKey = 'clinical' | 'contact' | 'address' | 'administrative';

// Everything starts collapsed so the reference info doesn't push the rest of
// the chart down the page on every visit.
const DEFAULT_COLLAPSED: Record<SectionKey, boolean> = {
  clinical: true,
  contact: true,
  address: true,
  administrative: true,
};

// Scope the persisted choices to the authenticated user so a doctor's
// open/closed selections follow their account on this device. Key shape kept
// stable so previously saved card choices still load.
const storageKeyForUser = (userId: string | number): string =>
  `patientInfoCards_collapsed_${userId}`;

const readCollapsed = (userId: string | number): Record<SectionKey, boolean> => {
  try {
    const stored = localStorage.getItem(storageKeyForUser(userId));
    // Merge over defaults so older saved values (which lacked `clinical`)
    // still load, with any missing section defaulting to collapsed.
    if (stored) return { ...DEFAULT_COLLAPSED, ...JSON.parse(stored) };
  } catch { /* noop */ }
  return DEFAULT_COLLAPSED;
};

export interface PatientSectionCollapse {
  collapsed: Record<SectionKey, boolean>;
  toggleSection: (key: SectionKey) => void;
  setAllSections: (nextCollapsed: boolean) => void;
  allExpanded: boolean;
}

// Single source of truth for the collapse state. Call once in the parent and
// pass the result down so the strip and the cards share one state.
export function usePatientSectionCollapse(): PatientSectionCollapse {
  const userId = useAuthStore((s) => s.user?.id ?? s.user?.email ?? 'default');

  const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>(() => readCollapsed(userId));

  // Reload persisted choices if the authenticated user changes (e.g. after
  // logging in or switching accounts) so one user's state doesn't leak.
  useEffect(() => {
    setCollapsed(readCollapsed(userId));
  }, [userId]);

  const persist = (next: Record<SectionKey, boolean>) => {
    try {
      localStorage.setItem(storageKeyForUser(userId), JSON.stringify(next));
    } catch { /* noop */ }
  };

  const toggleSection = (key: SectionKey) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      persist(next);
      return next;
    });
  };

  const setAllSections = (nextCollapsed: boolean) => {
    const next: Record<SectionKey, boolean> = {
      clinical: nextCollapsed,
      contact: nextCollapsed,
      address: nextCollapsed,
      administrative: nextCollapsed,
    };
    persist(next);
    setCollapsed(next);
  };

  const allExpanded =
    !collapsed.clinical && !collapsed.contact && !collapsed.address && !collapsed.administrative;

  return { collapsed, toggleSection, setAllSections, allExpanded };
}
